import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { api, type TenantUserListItem } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';

export function AdminTenantUsersPage() {
  const queryClient = useQueryClient();
  const { isAdmin, activeTenantId, tenantList, setTenantList } = useTenant();

  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(activeTenantId);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newRole, setNewRole] = useState<'manager' | 'user'>('user');
  const [newIsPrimary, setNewIsPrimary] = useState(false);
  const [editingRoles, setEditingRoles] = useState<Record<string, 'manager' | 'user'>>({});

  const tenantsQuery = useQuery({
    queryKey: ['admin-tenants', 'users-page'],
    queryFn: () =>
      api.listTenants({
        page: 1,
        pageSize: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!tenantsQuery.data) {
      return;
    }

    setTenantList(tenantsQuery.data.data);
  }, [setTenantList, tenantsQuery.data]);

  const effectiveTenantId = selectedTenantId ?? tenantList[0]?.id ?? null;

  const usersQuery = useQuery({
    queryKey: ['admin-users', 'for-assignment'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, sortBy: 'name', sortOrder: 'asc' }),
    enabled: isAdmin,
  });

  const assignmentsQuery = useQuery({
    queryKey: ['tenant-assignments', effectiveTenantId],
    queryFn: () => api.listTenantUsers(effectiveTenantId!),
    enabled: isAdmin && !!effectiveTenantId,
  });

  const assignMutation = useMutation({
    mutationFn: (payload: {
      tenantId: string;
      userId: string;
      role: 'manager' | 'user';
      isPrimary: boolean;
    }) =>
      api.assignTenantUser(payload.tenantId, {
        userId: payload.userId,
        role: payload.role,
        isPrimary: payload.isPrimary,
      }),
    onSuccess: () => {
      toast.success('User assigned to tenant');
      setSelectedUserId('');
      setNewRole('user');
      setNewIsPrimary(false);
      queryClient.invalidateQueries({ queryKey: ['tenant-assignments', effectiveTenantId] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to assign user');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({
      tenantId,
      userId,
      role,
    }: {
      tenantId: string;
      userId: string;
      role: 'manager' | 'user';
    }) => api.updateTenantUserRole(tenantId, userId, role),
    onSuccess: () => {
      toast.success('Role updated');
      queryClient.invalidateQueries({ queryKey: ['tenant-assignments', effectiveTenantId] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to update role');
    },
  });

  const removeMutation = useMutation({
    mutationFn: ({ tenantId, userId }: { tenantId: string; userId: string }) =>
      api.removeTenantUser(tenantId, userId),
    onSuccess: () => {
      toast.success('User removed from tenant');
      queryClient.invalidateQueries({ queryKey: ['tenant-assignments', effectiveTenantId] });
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to remove user');
    },
  });

  const assignmentRows = assignmentsQuery.data?.data;

  const availableUsers = useMemo(() => {
    const assignedIds = new Set((assignmentRows ?? []).map(item => item.userId));
    return (usersQuery.data?.data ?? []).filter(user => !assignedIds.has(user.id));
  }, [assignmentRows, usersQuery.data]);

  const handleAssign = (event: FormEvent) => {
    event.preventDefault();

    if (!effectiveTenantId || !selectedUserId) {
      toast.error('Select a tenant and user');
      return;
    }

    assignMutation.mutate({
      tenantId: effectiveTenantId,
      userId: selectedUserId,
      role: newRole,
      isPrimary: newIsPrimary,
    });
  };

  const getRowRole = (row: TenantUserListItem): 'manager' | 'user' => {
    return editingRoles[row.userId] ?? row.role;
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Tenant User Assignments</h1>
          <p className="text-muted-foreground">Admin access is required to view this page.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tenant User Assignments</h1>
          <p className="text-muted-foreground mt-1">
            Assign users to tenants and manage tenant-scoped roles
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Select Tenant</h2>
          <Select
            value={effectiveTenantId ?? ''}
            onChange={event => setSelectedTenantId(event.target.value || null)}
            className="max-w-md"
          >
            <option value="">Select tenant</option>
            {tenantList.map(tenant => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Assign User</h2>
          <form className="grid gap-3 md:grid-cols-4" onSubmit={handleAssign}>
            <Select
              value={selectedUserId}
              onChange={event => setSelectedUserId(event.target.value)}
              disabled={!effectiveTenantId}
              className="md:col-span-2"
            >
              <option value="">Select user</option>
              {availableUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </Select>

            <Select
              value={newRole}
              onChange={event => setNewRole(event.target.value as 'manager' | 'user')}
              disabled={!effectiveTenantId}
            >
              <option value="user">User</option>
              <option value="manager">Manager</option>
            </Select>

            <Button type="submit" disabled={!effectiveTenantId || assignMutation.isPending}>
              {assignMutation.isPending ? 'Assigning...' : 'Assign'}
            </Button>

            <label className="flex items-center gap-2 text-sm text-foreground md:col-span-4">
              <input
                type="checkbox"
                checked={newIsPrimary}
                onChange={event => setNewIsPrimary(event.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Make primary assignment
            </label>
          </form>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <h2 className="text-lg font-semibold text-foreground">Current Assignments</h2>

          {assignmentsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading assignments...</p>
          ) : assignmentsQuery.isError ? (
            <p className="text-sm text-red-600">
              {assignmentsQuery.error instanceof Error
                ? assignmentsQuery.error.message
                : 'Failed to load assignments'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Global Role</th>
                    <th className="py-2 pr-3">Tenant Role</th>
                    <th className="py-2 pr-3">Primary</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(assignmentRows ?? []).map(row => (
                    <tr key={row.id} className="border-b border-border last:border-b-0">
                      <td className="py-2 pr-3 align-top">
                        <div className="font-medium text-foreground">{row.user.name}</div>
                        <div className="text-xs text-muted-foreground">{row.user.email}</div>
                      </td>
                      <td className="py-2 pr-3 align-top text-muted-foreground">
                        {row.user.globalRole}
                      </td>
                      <td className="py-2 pr-3 align-top">
                        <Select
                          value={getRowRole(row)}
                          onChange={event =>
                            setEditingRoles(prev => ({
                              ...prev,
                              [row.userId]: event.target.value as 'manager' | 'user',
                            }))
                          }
                          className="max-w-[140px]"
                        >
                          <option value="user">User</option>
                          <option value="manager">Manager</option>
                        </Select>
                      </td>
                      <td className="py-2 pr-3 align-top text-muted-foreground">
                        {row.isPrimary ? 'Yes' : 'No'}
                      </td>
                      <td className="py-2 align-top">
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (!effectiveTenantId) {
                                return;
                              }

                              updateRoleMutation.mutate({
                                tenantId: effectiveTenantId,
                                userId: row.userId,
                                role: getRowRole(row),
                              });
                            }}
                            disabled={!effectiveTenantId || updateRoleMutation.isPending}
                          >
                            Save role
                          </Button>

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (!effectiveTenantId) {
                                return;
                              }

                              if (!window.confirm(`Remove ${row.user.name} from this tenant?`)) {
                                return;
                              }

                              removeMutation.mutate({
                                tenantId: effectiveTenantId,
                                userId: row.userId,
                              });
                            }}
                            disabled={!effectiveTenantId || removeMutation.isPending}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
